import { useMemo } from "react";
import { useACLContext } from "../context";
import type { PermissionAction, ResourceType } from "@shield-acl/core";

/**
 * Hook para verificação simples de permissão com memoização
 *
 * @param action - Ação a verificar
 * @param resource - Recurso opcional
 * @param context - Contexto adicional opcional
 * @returns boolean indicando se tem permissão
 *
 * @example
 * ```tsx
 * // Verificação simples
 * const canCreatePost = useCan("posts.create")
 *
 * // Com recurso
 * const canEditUser = useCan("users.edit", "users")
 *
 * // Com contexto
 * const canEditSpecificPost = useCan("posts.edit", "posts", { postId: 123 })
 *
 * if (canCreatePost) {
 *   return <CreatePostButton />
 * }
 * ```
 */
export function useCan<TContext = any>(
  action: PermissionAction,
  resource?: ResourceType,
  context?: TContext,
): boolean {
  const { can, user } = useACLContext<TContext>();

  // Memoiza o resultado baseado nos parâmetros e no usuário
  return useMemo(() => {
    return can(action, resource, context);
  }, [can, action, resource, context, user]);
}

/**
 * Hook para verificação negativa de permissão com memoização
 * Útil quando você quer mostrar algo quando o usuário NÃO tem permissão
 *
 * @param action - Ação a verificar
 * @param resource - Recurso opcional
 * @param context - Contexto adicional opcional
 * @returns boolean indicando se NÃO tem permissão
 *
 * @example
 * ```tsx
 * const cannotDelete = useCannot("posts.delete", "posts", { postId: 123 })
 *
 * if (cannotDelete) {
 *   return <DisabledDeleteButton />
 * }
 * ```
 */
export function useCannot<TContext = any>(
  action: PermissionAction,
  resource?: ResourceType,
  context?: TContext,
): boolean {
  const canResult = useCan(action, resource, context);
  return !canResult;
}
